import React, { MouseEvent, useState, useRef, useEffect, useMemo } from 'react';
import { WIDTH_CONSTANT } from './trim-timeline';
import { TrimTime } from './video-editor';

const HANDLES = {
	LEFT: 1,
	RIGHT: 2,
};

interface Props {
	secondsInterval: number,
	trimTimes: TrimTime,
	trimTimeChange: Function,
	thumbTrackWidth: number,
	bgStyle: {
		backgroundImage: string,
		width: number
	};
}

export default function TrimDragBox({
	secondsInterval,
	trimTimes,
	trimTimeChange,
	thumbTrackWidth,
	bgStyle,
}: Props) {
	const boxRef = useRef<null | HTMLDivElement>(null);
	const wrapperRef = useRef<null | HTMLDivElement>(null);
	const trimDragBoxRef = useRef<null | HTMLDivElement>(null);
	
	const [drag, setDrag] = useState(false);
	const [dragInitialX, setDragInitialX] = useState(0);
	
	const [resize, setResize] = useState(false);
	const [mouseInitialX, setMouseInitialX] = useState(0);
	const [whichHandle, setWhichHandle] = useState<null | number>(null);
	
	const [boxCurrentX, setBoxCurrentX] = useState(0);
	const [boxCurrentWidth, setBoxCurrentWidth] = useState(0);

	useEffect(() => {
		//update width based on trim time and zoom level
		const leftDist = trimTimes.start / secondsInterval * WIDTH_CONSTANT;
		const nWidth = (trimTimes.end - trimTimes.start) / secondsInterval * WIDTH_CONSTANT;
		if(boxRef.current) {
			boxRef.current.style.left = leftDist + 'px';
			boxRef.current.style.width = nWidth + 'px';
			boxRef.current.style.minWidth = nWidth + 'px';
		}

		if(trimDragBoxRef.current) {
			trimDragBoxRef.current.style.backgroundPositionX = -leftDist + 'px';
		}
	}, [trimTimes, secondsInterval]);

	const dragMouseDown = (e: MouseEvent) => {
		setMouseUpListener();
		setDrag(true);
		setDragInitialX(e.pageX);
		setBoxCurrentX(Number(boxRef.current?.style.left.replace('px', '')));
	};

	const dragMouseMove = (e: MouseEvent) => {
		if (drag && boxRef.current) {
			let newLeft = boxCurrentX + e.pageX - dragInitialX;
			if(newLeft < 0) {
				//clamp to left boundary
				newLeft = 0;
			} else if (newLeft + boxRef.current?.clientWidth > thumbTrackWidth) {
				//clamp to right boundary
				newLeft = thumbTrackWidth - boxRef.current?.clientWidth;
			}

			boxRef.current.style.left = newLeft + 'px';

			if (trimDragBoxRef.current) {
				trimDragBoxRef.current.style.backgroundPositionX = -newLeft + 'px';
			}
		}
	};

	const leftHandleDown = (e: MouseEvent) => {
		setMouseUpListener();
		setWhichHandle(HANDLES.LEFT);
		trimHandleMDown(e);
		setBoxCurrentX(Number(boxRef.current?.style.left.replace('px', '')));
	};
	
	const rightHandleDown = (e: MouseEvent) => {
		setMouseUpListener();
		setWhichHandle(HANDLES.RIGHT);
		trimHandleMDown(e);
	};

	const trimHandleMDown = (e: MouseEvent) => {
		if(boxRef.current) {
			setResize(true);
			setMouseInitialX(e.pageX);
			setBoxCurrentWidth(boxRef.current?.clientWidth);
		}
	};
	
	const resizeMouseMove = (e: MouseEvent) => {
		if (resize && boxRef.current) {
			const distanceMoved = e.pageX - mouseInitialX;
			
			if (whichHandle === HANDLES.LEFT) {
				//left handle have to make box smaller, and move left
				let newLeft = boxCurrentX + distanceMoved;
				let newWidth = boxCurrentWidth - distanceMoved;
				
				if (newLeft < 0) {
					//don't add overflow drag distance to width
					newWidth += newLeft;
					//clamp left boundary
					newLeft = 0;
				} else if (newLeft > thumbTrackWidth) {
					//clamp left drag handle to right boundary
					newLeft = thumbTrackWidth;
				}
				
				boxRef.current.style.minWidth = newWidth + 'px';
				boxRef.current.style.width = newWidth + 'px';
				boxRef.current.style.left = newLeft + 'px';
				if(trimDragBoxRef.current) {
					trimDragBoxRef.current.style.backgroundPositionX = -newLeft + 'px';
				}

				applyTrimState();
			} else if (whichHandle === HANDLES.RIGHT) {
				//right handle just have to change width
				const currentLeft = Number(boxRef.current?.style.left.replace('px', ''));
				
				let newWidth = boxCurrentWidth + distanceMoved;
				if((newWidth + currentLeft) > thumbTrackWidth) {
					//clamp right boundary
					newWidth = thumbTrackWidth - currentLeft;
				}
				boxRef.current.style.minWidth = newWidth + 'px';
				boxRef.current.style.width = newWidth + 'px';

				applyTrimState();
			}
		}
	};

	const setMouseUpListener = () => {
		window.addEventListener('mouseup', onMouseUp);
	};

	const applyTrimState = () => {
		if(boxRef.current) {
			const pxFromLeft = Number(boxRef.current.style.left.replace('px', ''));
			const width = boxRef.current.clientWidth;
			const end = (pxFromLeft + width) / WIDTH_CONSTANT * secondsInterval;
			let start = pxFromLeft / WIDTH_CONSTANT * secondsInterval;
			if (start < 0) {
				start = 0;
			}
			trimTimeChange({ start, end });
		}
	};
	
	const onMouseUp = () => {
		window.removeEventListener('mouseup', onMouseUp);

		setDrag(false);
		setDragInitialX(0);
		
		setResize(false);
		setMouseInitialX(0);

		if(boxRef.current) {
			setBoxCurrentWidth(boxRef.current?.clientWidth);
			applyTrimState();
		}
	};

	const onMouseMove = (e: MouseEvent) => {
		if(drag) {
			dragMouseMove(e);
		} else if(resize) {
			resizeMouseMove(e);
		}
	};

	return (
		<div
			className="trim-drag-box-wrap"
			onMouseMove={onMouseMove}
			ref={wrapperRef}
			style={{ minWidth: thumbTrackWidth + 'px' }}
		>
			<div
				ref={boxRef}
				className="trim-drag-box"
				id="trim-drag-box"
			>
				<div
					className='trim-resize-handle left'
					onMouseDown={leftHandleDown}
				>
					<span className="handle-bar"/>
					<span className="handle-bar" />
				</div>
				<div
					className='trim-drag-box-drag-area'
					onMouseDown={dragMouseDown}
					ref={trimDragBoxRef}
					style={bgStyle}
				/>
				<div
					className='trim-resize-handle right'
					onMouseDown={rightHandleDown}
				>
					<span className="handle-bar" />
					<span className="handle-bar" />
				</div>

			</div>
		</div>
	);
}